import { Ability, AbilityBuilder, AbilityClass, ExtractSubjectType, InferSubjects, PureAbility } from '@casl/ability';
import { Permission, RoleType } from '@hr-outsourcing/shared';
import { User } from '@/state/authState';
export type Actions =
  | 'create'
  | 'read'
  | 'update'
  | 'delete'
  | 'manage'
  | 'readAll'
  | 'updateAll'
  | 'readOwn'
  | 'createOwn'
  | 'approve'
  | 'evaluate'
  | 'updateStatus'
  | 'deleteAll'
  | 'manageApproved'
  | 'kvkk_manage'
  | 'readSalary'
  | 'updateSalary'
  | 'accessView';
export type Subjects =
  | 'Employee'
  | 'Candidate'
  | 'Position'
  | 'Title'
  | 'Skill'
  | 'University'
  | 'Company'
  | 'Leave'
  | 'Role'
  | 'CustomerCompany'
  | 'Announcement'
  | 'CompanyAnnouncement'
  | 'Expense'
  | 'Overtime'
  | 'Interview'
  | 'LeavePolicy'
  | 'Group'
  | 'GroupMember'
  | 'Form360Template'
  | 'Form360Evaluation'
  | 'Team'
  | 'EmployeeManager'
  | 'PolicyDocument'
  | 'Goal'
  | 'CLevelHierarchy'
  | 'OrganizationHierarchy'
  | 'AccountSettings'
  | 'RecruitmentReport'
  | 'Offboarding'
  | 'Inventory'
  | 'JobListing'
  | 'JobApplication'
  | 'all';
export type AppAbility = PureAbility<[Actions, Subjects]>;
export const AppAbility = Ability as AbilityClass<AppAbility>;
function permissionToCaslRule(permission: Permission): {
  action: Actions;
  subject: Subjects;
} | null {
  const permissionMap: Record<
    Permission,
    {
      action: Actions;
      subject: Subjects;
    }
  > = {
    [Permission.EMPLOYEE_CREATE]: { action: 'create', subject: 'Employee' },
    [Permission.EMPLOYEE_UPDATE_ALL]: { action: 'updateAll', subject: 'Employee' },
    [Permission.EMPLOYEE_READ_ALL]: { action: 'readAll', subject: 'Employee' },
    [Permission.EMPLOYEE_DELETE]: { action: 'delete', subject: 'Employee' },
    [Permission.EMPLOYEE_READ_SALARY]: { action: 'readSalary', subject: 'Employee' },
    [Permission.EMPLOYEE_UPDATE_SALARY]: { action: 'updateSalary', subject: 'Employee' },
    [Permission.CANDIDATE_CREATE]: { action: 'create', subject: 'Candidate' },
    [Permission.CANDIDATE_UPDATE]: { action: 'update', subject: 'Candidate' },
    [Permission.CANDIDATE_READ]: { action: 'read', subject: 'Candidate' },
    [Permission.CANDIDATE_DELETE]: { action: 'delete', subject: 'Candidate' },
    [Permission.POSITION_CREATE]: { action: 'create', subject: 'Position' },
    [Permission.POSITION_UPDATE]: { action: 'update', subject: 'Position' },
    [Permission.POSITION_READ]: { action: 'read', subject: 'Position' },
    [Permission.POSITION_DELETE]: { action: 'delete', subject: 'Position' },
    [Permission.TITLE_CREATE]: { action: 'create', subject: 'Title' },
    [Permission.TITLE_UPDATE]: { action: 'update', subject: 'Title' },
    [Permission.TITLE_READ]: { action: 'read', subject: 'Title' },
    [Permission.TITLE_DELETE]: { action: 'delete', subject: 'Title' },
    [Permission.SKILL_CREATE]: { action: 'create', subject: 'Skill' },
    [Permission.SKILL_UPDATE]: { action: 'update', subject: 'Skill' },
    [Permission.SKILL_READ]: { action: 'read', subject: 'Skill' },
    [Permission.SKILL_DELETE]: { action: 'delete', subject: 'Skill' },
    [Permission.COMPANY_CREATE]: { action: 'create', subject: 'Company' },
    [Permission.COMPANY_UPDATE]: { action: 'update', subject: 'Company' },
    [Permission.COMPANY_READ]: { action: 'read', subject: 'Company' },
    [Permission.COMPANY_DELETE]: { action: 'delete', subject: 'Company' },
    [Permission.LEAVES_APPROVE]: { action: 'approve', subject: 'Leave' },
    [Permission.LEAVES_READ_ALL]: { action: 'readAll', subject: 'Leave' },
    [Permission.LEAVES_CREATE_OWN]: { action: 'createOwn', subject: 'Leave' },
    [Permission.LEAVES_READ_OWN]: { action: 'readOwn', subject: 'Leave' },
    [Permission.ROLES_CREATE]: { action: 'create', subject: 'Role' },
    [Permission.ROLES_UPDATE]: { action: 'update', subject: 'Role' },
    [Permission.ROLES_READ]: { action: 'read', subject: 'Role' },
    [Permission.ROLES_DELETE]: { action: 'delete', subject: 'Role' },
    [Permission.CUSTOMER_COMPANY_READ]: { action: 'read', subject: 'CustomerCompany' },
    [Permission.CUSTOMER_COMPANY_MANAGE]: { action: 'manage', subject: 'CustomerCompany' },
    [Permission.ANNOUNCEMENT_MANAGE]: { action: 'manage', subject: 'Announcement' },
    [Permission.EXPENSE_READ_ALL]: { action: 'readAll', subject: 'Expense' },
    [Permission.EXPENSE_UPDATE_STATUS]: { action: 'updateStatus', subject: 'Expense' },
    [Permission.EXPENSE_DELETE_ALL]: { action: 'deleteAll', subject: 'Expense' },
    [Permission.OVERTIME_EVALUATE]: { action: 'evaluate', subject: 'Overtime' },
    [Permission.OVERTIME_READ_ALL]: { action: 'readAll', subject: 'Overtime' },
    [Permission.OVERTIME_MANAGE_APPROVED]: { action: 'manageApproved', subject: 'Overtime' },
    [Permission.INTERVIEW_MANAGE]: { action: 'manage', subject: 'Interview' },
    [Permission.INTERVIEW_CREATE]: { action: 'create', subject: 'Interview' },
    [Permission.INTERVIEW_READ]: { action: 'read', subject: 'Interview' },
    [Permission.LEAVE_POLICY_MANAGE]: { action: 'manage', subject: 'LeavePolicy' },
    [Permission.GROUP_CREATE]: { action: 'create', subject: 'Group' },
    [Permission.GROUP_READ_ALL]: { action: 'readAll', subject: 'Group' },
    [Permission.GROUP_DELETE]: { action: 'delete', subject: 'Group' },
    [Permission.GROUP_UPDATE]: { action: 'update', subject: 'Group' },
    [Permission.GROUP_MANAGE]: { action: 'manage', subject: 'Group' },
    [Permission.GROUP_MEMBER_ADD]: { action: 'create', subject: 'GroupMember' },
    [Permission.GROUP_MEMBER_REMOVE]: { action: 'delete', subject: 'GroupMember' },
    [Permission.GROUP_MEMBER_UPDATE]: { action: 'update', subject: 'GroupMember' },
    [Permission.GROUP_MEMBER_READ]: { action: 'read', subject: 'GroupMember' },
    [Permission.FORM360_TEMPLATE_READ]: { action: 'read', subject: 'Form360Template' },
    [Permission.FORM360_TEMPLATE_CREATE]: { action: 'create', subject: 'Form360Template' },
    [Permission.FORM360_TEMPLATE_UPDATE]: { action: 'update', subject: 'Form360Template' },
    [Permission.FORM360_TEMPLATE_DELETE]: { action: 'delete', subject: 'Form360Template' },
    [Permission.FORM360_EVALUATION_READ]: { action: 'read', subject: 'Form360Evaluation' },
    [Permission.FORM360_EVALUATION_CREATE]: { action: 'create', subject: 'Form360Evaluation' },
    [Permission.FORM360_EVALUATION_UPDATE]: { action: 'update', subject: 'Form360Evaluation' },
    [Permission.FORM360_EVALUATION_DELETE]: { action: 'delete', subject: 'Form360Evaluation' },
    [Permission.TEAM_CREATE]: { action: 'create', subject: 'Team' },
    [Permission.EMPLOYEE_MANAGER_READ]: { action: 'read', subject: 'EmployeeManager' },
    [Permission.EMPLOYEE_MANAGER_ASSIGN]: { action: 'create', subject: 'EmployeeManager' },
    [Permission.EMPLOYEE_MANAGER_REMOVE]: { action: 'delete', subject: 'EmployeeManager' },
    [Permission.ANNOUNCEMENT_COMPANY_MANAGE]: { action: 'manage', subject: 'CompanyAnnouncement' },
    [Permission.CANDIDATE_KVKK_MANAGE]: { action: 'kvkk_manage', subject: 'Candidate' },
    [Permission.UNIVERSITY_CREATE]: { action: 'create', subject: 'University' },
    [Permission.UNIVERSITY_READ]: { action: 'read', subject: 'University' },
    [Permission.UNIVERSITY_UPDATE]: { action: 'update', subject: 'University' },
    [Permission.UNIVERSITY_DELETE]: { action: 'delete', subject: 'University' },
    [Permission.GOAL_READ]: { action: 'read', subject: 'Goal' },
    [Permission.GOAL_CREATE]: { action: 'create', subject: 'Goal' },
    [Permission.GOAL_UPDATE]: { action: 'update', subject: 'Goal' },
    [Permission.GOAL_DELETE]: { action: 'delete', subject: 'Goal' },
    [Permission.GOAL_KEY_RESULT_APPROVE]: { action: 'approve', subject: 'Goal' },
    [Permission.GOAL_KEY_RESULT_UPDATE]: { action: 'update', subject: 'Goal' },
    [Permission.CLEVEL_HIERARCHY_MANAGE]: { action: 'update', subject: 'CLevelHierarchy' },
    [Permission.ORGANIZATION_HIERARCHY_MANAGE]: { action: 'update', subject: 'OrganizationHierarchy' },
    [Permission.ACCOUNT_SETTINGS_MANAGE]: { action: 'manage', subject: 'AccountSettings' },
    [Permission.RECRUITMENT_REPORT_READ]: { action: 'read', subject: 'RecruitmentReport' },
    [Permission.EMPLOYEE_OFFBOARDING_MANAGE]: { action: 'manage', subject: 'Offboarding' },
    [Permission.INVENTORY_READ_ALL]: { action: 'readAll', subject: 'Inventory' },
    [Permission.INVENTORY_CREATE]: { action: 'create', subject: 'Inventory' },
    [Permission.INVENTORY_UPDATE]: { action: 'update', subject: 'Inventory' },
    [Permission.INVENTORY_DELETE]: { action: 'delete', subject: 'Inventory' },
    [Permission.INVENTORY_APPROVE]: { action: 'approve', subject: 'Inventory' },
    [Permission.INVENTORY_ACCESS_VIEW]: { action: 'accessView', subject: 'Inventory' },
    [Permission.JOB_LISTING_MANAGE]: { action: 'manage', subject: 'JobListing' },
    [Permission.JOB_APPLICATION_READ]: { action: 'read', subject: 'JobApplication' },
    [Permission.JOB_APPLICATION_MANAGE]: { action: 'manage', subject: 'JobApplication' },
  };
  return permissionMap[permission] || null;
}
function permissionNameToCaslRule(permissionName: string): {
  action: Actions;
  subject: Subjects;
} | null {
  if (permissionName === 'universities:create') {
    return { action: 'create', subject: 'University' };
  }
  if (permissionName === 'universities:read') {
    return { action: 'read', subject: 'University' };
  }
  if (permissionName === 'universities:update') {
    return { action: 'update', subject: 'University' };
  }
  if (permissionName === 'universities:delete') {
    return { action: 'delete', subject: 'University' };
  }
  if (permissionName === 'candidate:kvkk:manage') {
    return { action: 'kvkk_manage', subject: 'Candidate' };
  }
  if (permissionName === 'form360:template:read') {
    return { action: 'read', subject: 'Form360Template' };
  }
  if (permissionName === 'form360:template:create') {
    return { action: 'create', subject: 'Form360Template' };
  }
  if (permissionName === 'form360:template:update') {
    return { action: 'update', subject: 'Form360Template' };
  }
  if (permissionName === 'form360:template:delete') {
    return { action: 'delete', subject: 'Form360Template' };
  }
  if (permissionName === 'form360:evaluation:read') {
    return { action: 'read', subject: 'Form360Evaluation' };
  }
  if (permissionName === 'form360:evaluation:create') {
    return { action: 'create', subject: 'Form360Evaluation' };
  }
  if (permissionName === 'form360:evaluation:update') {
    return { action: 'update', subject: 'Form360Evaluation' };
  }
  if (permissionName === 'form360:evaluation:delete') {
    return { action: 'delete', subject: 'Form360Evaluation' };
  }
  if (permissionName === 'announcements:manage') {
    return { action: 'manage', subject: 'Announcement' };
  }
  if (permissionName === 'announcements:company:manage') {
    return { action: 'manage', subject: 'CompanyAnnouncement' };
  }
  if (permissionName === 'groups:manage') {
    return { action: 'manage', subject: 'Group' };
  }
  if (permissionName === 'goals:create') {
    return { action: 'create', subject: 'Goal' };
  }
  if (permissionName === 'goals:delete') {
    return { action: 'delete', subject: 'Goal' };
  }
  if (permissionName === 'goals:read') {
    return { action: 'read', subject: 'Goal' };
  }
  if (permissionName === 'goals:update') {
    return { action: 'update', subject: 'Goal' };
  }
  if (permissionName === 'goals:key_result:approve') {
    return { action: 'approve', subject: 'Goal' };
  }
  return null;
}
export function defineAbilityFor(user: User | null): AppAbility {
  const { can, cannot, build } = new AbilityBuilder<AppAbility>(AppAbility);
  if (!user) {
    return build();
  }
  if (user.role?.name === RoleType.Admin) {
    can('manage', 'all');
    // AccountSettings is gated by explicit permission, not blanket admin access
    cannot('manage', 'AccountSettings');
    if (user.memberships) {
      for (const membership of user.memberships) {
        if (membership.permissions) {
          for (const perm of membership.permissions) {
            const permName = typeof perm === 'string' ? perm : (perm as any)?.name;
            if (permName === 'account-settings:manage') {
              can('manage', 'AccountSettings');
            }
          }
        }
      }
    }
    return build();
  }
  const permissions = user.permissions || [];
  for (const permission of permissions) {
    const rule = permissionToCaslRule(permission);
    if (rule) {
      can(rule.action, rule.subject);
    }
  }
  if (user.memberships) {
    for (const membership of user.memberships) {
      if (membership.permissions) {
        for (const perm of membership.permissions) {
          const permName = typeof perm === 'string' ? perm : (perm as any)?.name;
          if (!permName) continue;
          const nameRule = permissionNameToCaslRule(permName);
          if (nameRule) {
            can(nameRule.action, nameRule.subject);
          }
        }
      }
    }
  }
  return build();
}
export function canUserPerformAction(ability: AppAbility, permission: Permission): boolean {
  if (!ability) return false;
  const rule = permissionToCaslRule(permission);
  if (!rule) return false;
  return ability.can(rule.action, rule.subject);
}
export function canUserPerformAnyAction(ability: AppAbility, permissions: Permission[]): boolean {
  if (!ability) return false;
  return permissions.some((permission) => canUserPerformAction(ability, permission));
}
export function canUserPerformAllActions(ability: AppAbility, permissions: Permission[]): boolean {
  if (!ability) return false;
  return permissions.every((permission) => canUserPerformAction(ability, permission));
}
